'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

interface HeaderProps {
  className?: string;
}

const Header = ({ className = '' }: HeaderProps) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const navigationItems = [
    { label: 'Home', href: '/homepage', icon: 'HomeIcon' },
    { label: 'Our Process', href: '/our-process', icon: 'ClipboardDocumentCheckIcon' },
    { label: 'Service Plans', href: '/monthly-cleaning-plans', icon: 'RectangleStackIcon' },
    { label: 'About Us', href: '/about-us', icon: 'InformationCircleIcon' },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-card shadow-md' : 'bg-card'
      } ${className}`}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          <Link
            href="/homepage"
            className="flex items-center space-x-3 group"
            onClick={closeMobileMenu}
          >
            <div className="relative">
              <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center transform transition-transform duration-300 group-hover:scale-105">
                <svg
                  viewBox="0 0 40 40"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-8 h-8"
                >
                  <path
                    d="M20 8L8 16V32H16V24H24V32H32V16L20 8Z"
                    fill="currentColor"
                    className="text-primary-foreground"
                  />
                  <circle
                    cx="20"
                    cy="20"
                    r="2"
                    fill="currentColor"
                    className="text-accent"
                  />
                </svg>
              </div>
            </div>
            <div className="flex flex-col">
              <span className="text-2xl font-headline font-semibold text-foreground tracking-tight">
                Ordify Clean
              </span>
              <span className="text-xs font-body text-muted-foreground tracking-wide">
                Curated Excellence
              </span>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center space-x-1">
            {navigationItems.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className="flex items-center space-x-2 px-4 py-2 rounded-md text-foreground font-body font-medium transition-all duration-300 hover:bg-muted hover:text-primary"
              >
                <Icon name={item.icon as any} size={20} variant="outline" />
                <span>{item.label}</span>
              </Link>
            ))}
          </nav>

          <div className="hidden lg:flex items-center space-x-4">
            <Link
              href="/customer-application-form"
              className="px-6 py-2.5 bg-conversion text-conversion-foreground font-cta font-medium rounded-md transition-all duration-300 hover:bg-conversion/90 hover:shadow-elevated transform hover:-translate-y-0.5"
            >
              Start Application
            </Link>
          </div>

          <button
            onClick={toggleMobileMenu}
            className="lg:hidden p-2 rounded-md text-foreground hover:bg-muted transition-colors duration-300"
            aria-label="Toggle mobile menu"
          >
            <Icon
              name={isMobileMenuOpen ? 'XMarkIcon' : 'Bars3Icon'}
              size={24}
              variant="outline"
            />
          </button>
        </div>
      </div>

      {mounted && isMobileMenuOpen && (
        <div className="lg:hidden bg-card border-t border-border animate-slide-down">
          <nav className="container mx-auto px-4 py-4 space-y-2">
            {navigationItems.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                onClick={closeMobileMenu}
                className="flex items-center space-x-3 px-4 py-3 rounded-md text-foreground font-body font-medium transition-all duration-300 hover:bg-muted hover:text-primary"
              >
                <Icon name={item.icon as any} size={20} variant="outline" />
                <span>{item.label}</span>
              </Link>
            ))}
            <div className="pt-4 border-t border-border">
              <Link
                href="/customer-application-form"
                onClick={closeMobileMenu}
                className="flex items-center justify-center w-full px-6 py-3 bg-conversion text-conversion-foreground font-cta font-medium rounded-md transition-all duration-300 hover:bg-conversion/90"
              >
                Start Application
              </Link>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;